import React, { useMemo } from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../app/store';
import { useNavigate, useParams } from 'react-router-dom';
import { ProjectContentPlayer } from '../../../../app/components/audioPlayer/WaveSurfer/waveSurferProjectContent/ProjectContentPlayer';
import * as REQUEST_REQUIREMENTS from '../../../../app/api/requestRequirements';
import { toast } from 'react-toastify';
import { authenticateUserFromStorage } from '../../../../app/features/sessions/sessionSlice';
import {
    getProjectContents, patchCurrentProject, postProjectFile,
    takeProjectContentFromProject, refreshProjectContents,
} from '../../../../app/features/sessions/projectsManagement/sessionManageProject';
import MixProjectContents from './MixProjectContents';
import { clearSelection } from '../../../../app/features/sessions/sessionProjectManagementPlayer/sliceProjectManagementPlayer';
import { formatDuration } from '../../../../app/components/audioPlayer/WaveSurfer/waveSurferProjectContent/Duration';
import { FormCurrentProject } from './FormCurrentProject';
import { Switch as MuiSwitch } from '@mui/material';
import { EditMinutesSecondsCentiseconds } from '../../../../app/components/inputs/EditMinutesSecondsCentiseconds';
import { ButtonSelectAllUseful } from '../../../../app/components/audioPlayer/WaveSurfer/waveSurferProjectContent/ProjectContentsSelection';
import { ProjectPlayer } from '../../../../app/components/audioPlayer/WaveSurfer/projectPlayer/ProjectPlayer';
import {
    Box, Container, IconButton, Skeleton, Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MergeIcon from '@mui/icons-material/Merge';
import { ProjectContentInfo } from '../../../../app/components/modal/projectContent/info/ProjectContentInfo';
import { YesNoDialogModal } from '../../../../app/components/modal/dialogBox/group/yesNo/YesNoDialogModal';
import { ProjectContentCommentsModal } from '../../../../app/components/modal/projectContent/comments/ProjectContentCommentsModal';
import AudioContentRecorder from './AudioContentRecorder';
import CreateAudioContentRecorded from '../../../../app/components/modal/createAudioContentRecorded/CreateAudioContentRecorded';

// ── Design tokens ──────────────────────────────────────────────────────────────
const BG_MAIN        = '#15151a';
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

export default function ManageProject() {
    const currentUser                    = useSelector((state: RootState) => state.session.currentUser);
    const authHeaders                    = useSelector((state: RootState) => state.session.authHeaders);
    const userValidated                  = useSelector((state: RootState) => state.session.loggedIn);
    const currentUsefulProjectContents   = useSelector((state: RootState) => state.sessionManageProjectContents.usefulProjectContents);
    const currentUselessProjectContents  = useSelector((state: RootState) => state.sessionManageProjectContents.uselessProjectContents);
    const newUsefulProjectContents       = useSelector((state: RootState) => state.sessionManageProjectContents.newUsefulProjectContents);
    const loading                        = useSelector((state: RootState) => state.sessionProjectPlayerManagement.loading || state.sessionManageProjectContents.loading);
    const projectDuration                = useSelector((state: RootState) => state.sessionManageProjectContents.currentProject.project.projectDuration);
    const currentProject                 = useSelector((state: RootState) => state.sessionManageProjectContents.currentProject.project);
    const currentProjectPrivacity        = useSelector((state: RootState) => state.sessionManageProjectContents.currentProject.project.privacity);
    const masterUrl                      = useSelector((state: RootState) => state.sessionManageProjectContents.masterFiles);
    const loadingScreenUseless           = useSelector((state: RootState) => state.sessionManageProjectContents.loadingScreenUseless);
    const loadingScreenUseful            = useSelector((state: RootState) => state.sessionManageProjectContents.loadingScreenUseful);
    const mergedAudioUrl                 = useSelector((state: RootState) => state.sessionProjectPlayerManagement.mergedAudioUrl);
    const somethingSelected              = useSelector((state: RootState) => state.sessionManageProjectContents.somethingSelected);

    const projectDurationFormated = formatDuration(projectDuration);

    const [editTitleClicked, setEditTitleClicked]                         = useState(false);
    const [currentProjectIsPublic, setCurrentProjectIsPublic]             = useState(currentProjectPrivacity === 0);
    const [mixedAudioUrl, setMixedAudioUrl]                               = useState<string | undefined>('');
    const [projectContentIdInfoSelected, setProjectContentIdInfoSelected] = useState<string>('');
    const [showProjectContentInfoModal, setShowProjectContentInfoModal]   = useState<boolean>(false);
    const [openYesNoRemoveContentModal, setOpenYesNoRemoveContentModal]   = useState<boolean>(false);
    const [projectContentToDelete, setProjectContentToDelete]             = useState<string>('');
    const [projectContentToComment, setProjectContentToComment]           = useState<string>('');
    const [showProjectContentCommentModal, setShowProjectContentCommentModal] = useState<boolean>(false);
    const [showFormAudioRecordedCreation, setShowFormAudioRecordedCreation] = useState(false);
    const [audioRecordedUrl, setAudioRecordedUrl]                         = useState<string | null>(null);

    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { userNicknameRoute, projectIdRoute } = useParams();

    const MemorizedProjectContentPlayer = useMemo(() => React.memo(ProjectContentPlayer), []);
    const MemorizedProjectPlayer        = useMemo(() => React.memo(ProjectPlayer), []);

    const handleOpenRecorderModal: React.Dispatch<React.SetStateAction<boolean>> = (isOpen) => {
        setShowFormAudioRecordedCreation(isOpen);
    };

    useEffect(() => {
        async function authenticateUser() {
            if (!userValidated) await dispatch(authenticateUserFromStorage());
        }
        async function getCurrentProjectContents() {
            if (projectIdRoute !== undefined) {
                const response = await dispatch(getProjectContents({
                    authHeaders, userNickname: userNicknameRoute, projectId: parseInt(projectIdRoute),
                }));
                if (response.meta.requestStatus === 'rejected') {
                    const PRIVATE_ROUTES = REQUEST_REQUIREMENTS.handlePrivateRoutes({ ROUTE_PARAMS: { userNickname: currentUser?.nickname } });
                    if (PRIVATE_ROUTES) { toast.error('Error loading project contents'); navigate(PRIVATE_ROUTES.HOME); }
                    return;
                }
                if (response.payload.project.privacity !== 0 && response.payload.project['user_id'] !== currentUser?.id) {
                    const PRIVATE_ROUTES = REQUEST_REQUIREMENTS.handlePrivateRoutes({ ROUTE_PARAMS: { userNickname: currentUser?.nickname } });
                    navigate(PRIVATE_ROUTES.HOME);
                }
            }
        }
        dispatch(clearSelection());
        authenticateUser();
        getCurrentProjectContents();
        setCurrentProjectIsPublic(currentProjectPrivacity === 0);
    }, [userNicknameRoute, currentProjectIsPublic]);

    useEffect(() => {
        async function authenticateUser() {
            if (!userValidated) await dispatch(authenticateUserFromStorage());
        }
        async function getCurrentProjectContents() {
            if (projectIdRoute !== undefined) {
                const response = await dispatch(refreshProjectContents({
                    authHeaders, userNickname: userNicknameRoute, projectId: parseInt(projectIdRoute),
                }));
                if (response.meta.requestStatus === 'rejected') {
                    const PRIVATE_ROUTES = REQUEST_REQUIREMENTS.handlePrivateRoutes({ ROUTE_PARAMS: { userNickname: currentUser?.nickname } });
                    if (PRIVATE_ROUTES) { toast.error('Error loading project contents'); navigate(PRIVATE_ROUTES.HOME); }
                }
            }
        }
        authenticateUser();
        getCurrentProjectContents();
    }, [userNicknameRoute, currentProjectIsPublic]);

    async function handlePrivacity() {
        await dispatch(patchCurrentProject({
            authHeaders,
            projectRouteParams: { userNicknameRoute, projectIdRoute },
            patchCurrentProjectBody: { privacity: currentProjectPrivacity === 0 ? 1 : 0 },
        }));
    }

    async function handleCommitSketch() {
        await dispatch(postProjectFile({
            authHeaders,
            projectRouteParams: { userNicknameRoute, projectIdRoute },
            postCurrentProjectFileBody: { id: projectIdRoute, url: mixedAudioUrl },
        }));
    }

    function handleRemoveFromProject() {
        setOpenYesNoRemoveContentModal(false);
        dispatch(takeProjectContentFromProject({
            authHeaders,
            projectContentRouteParams: { userNicknameRoute, projectContentId: projectContentToDelete },
        }));
    }

    const renderContentItem = (item: any, index: number) => {
        if (!item.content) return null;
        return (
            <li key={index} style={{ overflow: 'visible', display: 'flex', padding: 0, height: '62px', alignItems: 'stretch', listStyle: 'none', flexShrink: 0 }}>
                {/* Instrument label — vertical rotated */}
                <Box sx={{
                    display: 'flex',
                    position: 'relative',
                    zIndex: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 36,
                    height: '62px',
                    flexShrink: 0,
                    bgcolor: BRAND_RED,
                    borderRadius: '4px 0 0 4px',
                }}>
                    <Typography component='span' sx={{
                        fontSize: '10px',
                        color: '#fff',
                        transform: 'rotate(-90deg)',
                        whiteSpace: 'nowrap',
                        userSelect: 'none',
                        fontWeight: 600,
                        lineHeight: 1,
                        display: 'block',
                    }}>
                        {item.content.instrument?.name ?? 'outro'}
                    </Typography>
                </Box>

                {/* Player + title overlay */}
                <Box sx={{ position: 'relative', flex: 1, minWidth: 0, height: '100%', display: 'flex', alignItems: 'stretch' }}>
                    {/* Title chip */}
                    <Box sx={{
                        position: 'absolute',
                        top: 2, left: 30, zIndex: 6,
                        bgcolor: 'rgba(0,0,0,0.72)',
                        color: TEXT_PRIMARY,
                        borderRadius: '2px',
                        px: 0.5, py: 0.25,
                        pointerEvents: 'none',
                    }}>
                        <Typography variant='caption' sx={{ userSelect: 'none', fontSize: '10px' }}>
                            {item.content.title}
                        </Typography>
                    </Box>
                    <MemorizedProjectContentPlayer
                        projectContentId={item.id}
                        metadataDurationCentiseconds={item.content.file?.blob.metadata.centiseconds}
                        fileUrl={`${item.fileUrl}`}
                        startsIn={item.startsIn}
                        isUseful={item.useful}
                        ownerId={item.content.userId}
                        showYesNoRemoveProjectFunction={setOpenYesNoRemoveContentModal}
                        functionSetIdToShowInfo={setProjectContentIdInfoSelected}
                        functionProjectIdToRemove={setProjectContentToDelete}
                        setShowInfoModal={setShowProjectContentInfoModal}
                        boxCommentsOpened={showProjectContentCommentModal}
                        setShowToggleBoxComments={setShowProjectContentCommentModal}
                        setProjectContentToComment={setProjectContentToComment}
                    />
                </Box>
            </li>
        );
    };

    // Outer container: clips all children (including transform stacking contexts) via overflow:hidden
    const timelineOuterSx = {
        border: BORDER_SUBTLE,
        borderRadius: '8px',
        height: 350,
        overflow: 'hidden',
        backgroundImage: `linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundColor: BG_CARD,
        backgroundSize: '10px 100%',
    };

    // Inner container: handles horizontal scroll without breaking the outer clip
    const timelineScrollSx = {
        overflowX: 'auto',
        overflowY: 'hidden',
        height: '100%',
        scrollbarWidth: 'thin',
        scrollbarColor: '#555 transparent',
    };

    const ulSx: React.CSSProperties = {
        listStyle: 'none',
        padding: 0,
        margin: 0,
        height: '100%',
        width: 'fit-content',
        minWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '1px',
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: BG_MAIN, py: 3 }}>
            {/* ── Modals ── */}
            {showProjectContentCommentModal && (
                <ProjectContentCommentsModal
                    projectContentShowedId={projectContentToComment}
                    currentUserNickname={currentUser?.nickname}
                    userNicknameRoute={userNicknameRoute}
                    onCloseClick={setShowProjectContentCommentModal}
                />
            )}
            {openYesNoRemoveContentModal && (
                <YesNoDialogModal
                    message='Do you want to <span id="text-remove-content-from-project">remove</span> this content from the project? This action cannot be undone.'
                    onYesClick={handleRemoveFromProject}
                    onCloseClick={setOpenYesNoRemoveContentModal}
                />
            )}
            {showProjectContentInfoModal && userNicknameRoute && (
                <ProjectContentInfo
                    projectContentId={projectContentIdInfoSelected}
                    userNicknameUrlParams={userNicknameRoute}
                    onCloseClick={setShowProjectContentInfoModal}
                />
            )}
            {showFormAudioRecordedCreation && (
                <CreateAudioContentRecorded
                    onClose={setShowFormAudioRecordedCreation}
                    audioRecorded={audioRecordedUrl}
                />
            )}

            {/* ── Fixed elements ── */}
            <Box sx={{ position: 'fixed', top: '68vh', right: '5vw', zIndex: 1000 }}>
                <AudioContentRecorder
                    onOpenRecorderModal={handleOpenRecorderModal}
                    onAudioRecordedUrl={setAudioRecordedUrl}
                />
            </Box>

            {loading ? (
                <Container maxWidth='xl'>
                    {/* Header skeleton */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Skeleton variant='rounded' width={260} height={48} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                            <Skeleton variant='circular' width={30} height={30} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                        </Box>
                        <Box sx={{ width: 40, height: 3, bgcolor: BRAND_RED }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                            <Skeleton variant='rounded' width={40} height={20} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                            <Skeleton variant='rounded' width={100} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                        </Box>
                    </Box>

                    {/* Controls row skeleton */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Skeleton variant='rounded' width={110} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                        <Skeleton variant='rounded' width={130} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                    </Box>

                    {/* Useful section skeleton */}
                    <Box sx={{ mb: 0.5 }}>
                        <Skeleton variant='rounded' width={120} height={16} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
                        <Box sx={{ width: 40, height: 2, bgcolor: BRAND_RED, mt: 0.5 }} />
                    </Box>
                    <Box sx={timelineOuterSx}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Box key={i} sx={{ display: 'flex', height: 62, flexShrink: 0 }}>
                                    <Box sx={{ width: 36, height: 62, bgcolor: 'rgba(192,57,43,0.25)', borderRadius: '4px 0 0 4px', flexShrink: 0 }} />
                                    <Skeleton variant='rectangular' sx={{ flex: 1, height: 62, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 0 }} />
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Staging section skeleton */}
                    <Box sx={{ mt: 3, mb: 0.5 }}>
                        <Skeleton variant='rounded' width={80} height={16} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
                        <Box sx={{ width: 40, height: 2, bgcolor: 'rgba(255,255,255,0.2)', mt: 0.5 }} />
                    </Box>
                    <Box sx={{ ...timelineOuterSx, mb: 20 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                            {Array.from({ length: 3 }).map((_, i) => (
                                <Box key={i} sx={{ display: 'flex', height: 62, flexShrink: 0 }}>
                                    <Box sx={{ width: 36, height: 62, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: '4px 0 0 4px', flexShrink: 0 }} />
                                    <Skeleton variant='rectangular' sx={{ flex: 1, height: 62, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 0 }} />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Container>
            ) : (
                <>
                    {somethingSelected && (
                        <MixProjectContents setMixedAudioUrl={setMixedAudioUrl} />
                    )}

                    <Container maxWidth='xl'>
                        {/* ── Header ── */}
                        <Box sx={{ mb: 3 }}>
                            {/* Title row */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                {editTitleClicked ? (
                                    <FormCurrentProject handleEditTitleClicked={() => setEditTitleClicked(false)} />
                                ) : (
                                    <Typography variant='h4' fontWeight={700} color={TEXT_PRIMARY}
                                        sx={{ wordBreak: 'break-word' }}>
                                        {currentProject.title}
                                    </Typography>
                                )}
                                {currentUser && currentProject && currentUser.id === currentProject.userId && (
                                    <IconButton
                                        size='small'
                                        onClick={() => setEditTitleClicked(!editTitleClicked)}
                                        sx={{ color: TEXT_SECONDARY, bgcolor: 'rgba(255,255,255,0.06)', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
                                    >
                                        <EditIcon fontSize='small' />
                                    </IconButton>
                                )}
                            </Box>
                            <Box sx={{ width: 40, height: 3, bgcolor: BRAND_RED }} />

                            {/* Stats + Privacy row */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <VisibilityIcon sx={{ fontSize: 18, color: TEXT_SECONDARY }} />
                                    <Typography variant='body2' color={TEXT_SECONDARY}>{currentProject.nVisits}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {(currentProjectPrivacity === 1 || currentUser?.nickname === userNicknameRoute) && (
                                        <Typography variant='body2' sx={{ fontStyle: 'italic', color: '#e57373', fontWeight: 600 }}>
                                            PRIVATE
                                        </Typography>
                                    )}
                                    {userNicknameRoute === currentUser?.nickname && (
                                        <MuiSwitch
                                            checked={currentProjectPrivacity === 0}
                                            onChange={handlePrivacity}
                                            disabled={Boolean(currentUser && currentUser.id !== currentProject.userId)}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#4caf50' },
                                                '& .MuiSwitch-switchBase': { color: '#e57373' },
                                                '& .MuiSwitch-track': { bgcolor: 'rgba(231,83,83,0.5)' },
                                            }}
                                        />
                                    )}
                                    {(currentProjectPrivacity === 0 || currentUser?.nickname === userNicknameRoute) && (
                                        <Typography variant='body2' sx={{ fontStyle: 'italic', color: '#4caf50', fontWeight: 600 }}>
                                            PUBLIC
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        {/* ── Master + Sketch tracks ── */}
                        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {masterUrl.length > 0 && (
                                <Box>
                                    <Typography variant='caption' color={TEXT_SECONDARY} sx={{ mb: 0.5, display: 'block' }}>
                                        Master
                                    </Typography>
                                    <MemorizedProjectPlayer fileUrl={`${masterUrl[0].signedUrl}`} />
                                </Box>
                            )}
                            {mixedAudioUrl !== '' && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant='caption' color={TEXT_SECONDARY} sx={{ mb: 0.5, display: 'block' }}>
                                            Sketch
                                        </Typography>
                                        <MemorizedProjectPlayer fileUrl={mergedAudioUrl} />
                                    </Box>
                                    {currentUser && currentProject.userId === currentUser.id && (
                                        <IconButton
                                            onClick={handleCommitSketch}
                                            sx={{
                                                bgcolor: BRAND_RED, color: '#fff',
                                                width: 52, height: 52, flexShrink: 0,
                                                '&:hover': { bgcolor: '#a93226' },
                                            }}
                                        >
                                            <MergeIcon />
                                        </IconButton>
                                    )}
                                </Box>
                            )}
                        </Box>

                        {/* ── Duration + Select All ── */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <ButtonSelectAllUseful />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant='body2' color={TEXT_SECONDARY}>Duration:</Typography>
                                <EditMinutesSecondsCentiseconds
                                    projectDurationFormated={projectDurationFormated}
                                    currentProject={currentProject}
                                />
                            </Box>
                        </Box>

                        {/* ── Useful contents ── */}
                        <Box sx={{ mb: 0.5 }}>
                            <Typography variant='overline' color={TEXT_SECONDARY} sx={{ letterSpacing: 2 }}>
                                Useful contents
                            </Typography>
                            <Box sx={{ width: 40, height: 2, bgcolor: BRAND_RED, mt: 0.25 }} />
                        </Box>
                        <Box sx={timelineOuterSx}>
                            <Box sx={timelineScrollSx}>
                                {loadingScreenUseful ? (
                                    <ul style={ulSx}>
                                        {Array.from({ length: 5 }).map((_, i) => (
                                            <li key={i} style={{ overflow: 'visible', display: 'flex', padding: 0, height: '62px', alignItems: 'stretch', listStyle: 'none', flexShrink: 0 }}>
                                                <Box sx={{ width: 36, height: 62, bgcolor: 'rgba(192,57,43,0.25)', borderRadius: '4px 0 0 4px', flexShrink: 0 }} />
                                                <Skeleton variant='rectangular' sx={{ flex: 1, height: 62, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 0 }} />
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <ul style={ulSx}>
                                        {newUsefulProjectContents?.map((item, index) => renderContentItem(item, index))}
                                        {currentUsefulProjectContents?.map((item, index) => renderContentItem(item, index))}
                                    </ul>
                                )}
                            </Box>
                        </Box>

                        {/* ── Staging (useless) contents ── */}
                        <Box sx={{ mt: 3, mb: 0.5 }}>
                            <Typography variant='overline' color={TEXT_SECONDARY} sx={{ letterSpacing: 2 }}>
                                Staging
                            </Typography>
                            <Box sx={{ width: 40, height: 2, bgcolor: 'rgba(255,255,255,0.2)', mt: 0.25 }} />
                        </Box>
                        <Box sx={{ ...timelineOuterSx, mb: 20 }}>
                            <Box sx={timelineScrollSx}>
                                {loadingScreenUseless ? (
                                    <ul style={ulSx}>
                                        {Array.from({ length: 3 }).map((_, i) => (
                                            <li key={i} style={{ overflow: 'visible', display: 'flex', padding: 0, height: '62px', alignItems: 'stretch', listStyle: 'none', flexShrink: 0 }}>
                                                <Box sx={{ width: 36, height: 62, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: '4px 0 0 4px', flexShrink: 0 }} />
                                                <Skeleton variant='rectangular' sx={{ flex: 1, height: 62, bgcolor: 'rgba(255,255,255,0.04)', borderRadius: 0 }} />
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <ul style={ulSx}>
                                        {currentUselessProjectContents?.map((item, index) => renderContentItem(item, index))}
                                    </ul>
                                )}
                            </Box>
                        </Box>
                    </Container>
                </>
            )}
        </Box>
    );
}
